<template>
  <svg :id="'velocity-chart-'+id" viewBox="0,0,750,300" preserveAspectRatio="xMaxYMax meet"></svg>
</template>

<script>
  import * as d3 from 'd3'
  import { sprintDate } from '@/components/mixins/util'

  export default {
    name: 'VelocityGraph',
    props: {
      id: {type: String, required: true},
      data: {type: Array, required: true},
    },
    methods: {
      draw: function() {
        let xExtent = [this.data[0].start, this.data[this.data.length-1].end]

        // Compute the extent of the y-axis by taking the max and min of d.added
        // and d.seen, and merging them. Then use 10% of the range as padding,
        // to keep the minimal points from hitting the bottom of the graph.
        let addedExtent = d3.extent(this.data, function(d) { return d.added }),
          seenExtent = d3.extent(this.data, function(d) { return d.seen })
        let yExtent = [
          Math.min(addedExtent[0], seenExtent[0]),
          Math.max(addedExtent[1], seenExtent[1]),
        ]
        let yRange = yExtent[1]-yExtent[0]
        if (yRange < 20) {
          yExtent[0] = Math.max(0, yExtent[0]-2)
          yExtent[1] = yExtent[1]+2
        } else {
          yExtent[0] = Math.floor(yExtent[0] - 0.1*yRange)
          yExtent[1] = Math.ceil(yExtent[1] + 0.1*yRange)
          yExtent[0] = Math.max(0, yExtent[0])
        }

        let x = d3.scaleLinear().domain(xExtent).range([55, 745])
        let y = d3.scaleLinear().domain(yExtent).range([295, 5])

        // Start drawing.
        let svg = d3.select('svg#velocity-chart-'+this.id)
        svg.selectAll('*').remove()

        let g = svg.append('g').attr('transform', 'translate(0, 0)')

        // Draw a hidden reticle.
        let reticleg = svg.append('g')
          .attr('transform', 'translate(0, 0)').attr('visibility', 'hidden')
        reticleg.append('line')
          .attr('x1', 0).attr('y1', 5).attr('x2', 0).attr('y2', 295)
          .attr('stroke', '#7b6888').attr('stroke-width', 1)

        let reticleArc = d3.arc().outerRadius(7).innerRadius(3)({startAngle: 0, endAngle: 2*Math.PI})
        let reticle1 = reticleg.append('path')
          .attr('fill', '#8a89a6').attr('opacity', 0.5).attr('d', reticleArc)
        let reticle2 = reticleg.append('path')
          .attr('fill', '#98abc5').attr('opacity', 0.5).attr('d', reticleArc)

        // Draw a tooltip with detailed information next to the reticle.
        let tooltipg = reticleg.append('g')
          .attr('transform', 'translate(25, 50)')

        let tooltipRect1 = tooltipg.append('rect')
          .attr('x', -6).attr('fill', '#e5e5e5')
        let tooltipRect2 = tooltipg.append('rect')
          .attr('x', -5).attr('fill', 'white')

        let tooltip1 = tooltipg.append('text')
          .attr('font-weight', 'bold').text('Date')
        let tooltipLabel1 = tooltipg.append('text')
          .attr('dx', '1.25em').attr('dy', '1.25em').text('Certs Seen')
        let tooltipLabel2 = tooltipg.append('text')
          .attr('dx', '1.25em').attr('dy', '2.5em').text('Tree Growth')

        let bbox1 = tooltip1._groups[0][0].getBBox()
        tooltipRect1.attr('y', bbox1.y-6)
        tooltipRect2.attr('y', bbox1.y-5)
        let bbox2 = tooltipLabel1._groups[0][0].getBBox(),
          y1 = bbox2.y + bbox2.height/2 - 7
        tooltipg.append('rect')
          .attr('x', 0).attr('y', y1).attr('width', 14).attr('height', 14)
          .attr('fill', '#e5e5e5')
        tooltipg.append('rect')
          .attr('x', 1).attr('y', y1+1).attr('width', 12).attr('height', 12)
          .attr('fill', '#f4f4f4')
        let bbox3 = tooltipLabel2._groups[0][0].getBBox(),
          y2 = bbox3.y + bbox3.height/2
        tooltipg.append('line')
          .attr('stroke', '#7b6888').attr('stroke-width', 2)
          .attr('x1', 0).attr('y1', y2).attr('x2', bbox3.x-3).attr('y2', y2)

        let tooltip2 = tooltipg.append('text')
          .attr('text-anchor', 'end').attr('dx', '11em').attr('dy', '1.25em')
        let tooltip3 = tooltipg.append('text')
          .attr('text-anchor', 'end').attr('dx', '11em').attr('dy', '2.5em')

        // Draw a histogram for the 'seen' dimension of the data.
        let bar = g.selectAll('.bar')
          .data(this.data)
          .enter().append('g')
            .attr('class', 'bar')
            .attr('transform', function(d) { return 'translate('+x(d.start)+', 0)'})
            .on('mouseenter', function(d) {
              reticle1.attr('transform', 'translate(0, '+y(d.seen)+')')
              reticle2.attr('transform', 'translate(0, '+y(d.added)+')')

              tooltip1.text(sprintDate(d.end))
              tooltip2.text(d.seen.toLocaleString())
              tooltip3.text(d.added.toLocaleString())

              let bbox = tooltip3._groups[0][0].getBBox()
              tooltipRect1.attr('width', bbox.x+bbox.width+12).attr('height', bbox.y+bbox.height-bbox1.y+12)
              tooltipRect2.attr('width', bbox.x+bbox.width+10).attr('height', bbox.y+bbox.height-bbox1.y+10)

              let rightSide = x(d.end) + 20 + bbox.x+bbox.width+12
              if (rightSide < 745) {
                tooltipg.attr('transform', 'translate(25, 50)')
              } else {
                let offsetX = -(20 + bbox.x+bbox.width+12)
                tooltipg.attr('transform', 'translate('+offsetX+', 50)')
              }

              reticleg.attr('transform', 'translate('+x(d.end)+', 0)')
                .attr('visibility', 'visible')
            })
            .on('mouseleave', function(d) { reticleg.attr('visibility', 'hidden') })
        bar.append('rect') // Invisible rect, for mouseover effects.
          .attr('width', function(d) { return x(d.end)-x(d.start) })
          .attr('height', 295).attr('fill', 'white')
        bar.append('rect') // Dark gray rect, gives illusion of 1px border.
          .attr('x', function(d, i) {return (i == 0 ? 0 : -1) })
          .attr('y', function(d) { return y(d.seen) })
          .attr('width', function(d) { return x(d.end)-x(d.start)+1 })
          .attr('height', function(d) { return 295-y(d.seen) })
          .attr('fill', '#e5e5e5')
        bar.append('rect') // Inner rect, alternates between light and dark.
          .attr('x', function(d, i) { return (i == 0 ? 1 : 0) })
          .attr('y', function(d) { return y(d.seen)+1 })
          .attr('width', function(d) { return x(d.end)-x(d.start)-1 })
          .attr('height', function(d) { return 295-y(d.seen)-1 })
          .attr('fill', function(d, i) { return (i%2 == 0 ? '#fafafa' : '#f4f4f4') })

        // Draw a line for the 'added' dimension of the data.
        let line = d3.line()
          .x(function(d) { return x(d.end) })
          .y(function(d) { return y(d.added) })
        g.append('path')
          .datum(this.data)
          .attr('fill', 'none').attr('stroke', '#7b6888').attr('stroke-width', 2)
          .attr('stroke-linejoin', 'round').attr('stroke-linecap', 'round')
          .attr('d', line)
          .on('mouseenter', function(d) {
            reticleg.attr('visibility', 'visible')
          })

        // Draw the legend for the x-axis (y-axis omitted because its not useful).
        g.append('g')
          .style('font-family', '\'Open Sans\', sans-serif')
          .style('font-size', '14px')
          .attr('transform', 'translate(55, 0)')
          .call(d3.axisLeft(y).ticks(3, 's'))
      },
    },

    watch: {
      data: function() { this.draw() },
    },
    mounted: function() { this.draw() },
  }
</script>

<style scoped>
  svg {
    /* Prevent browser from asking to save/copy image. */
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }
</style>



// WEBPACK FOOTER //
// src/components/widgets/VelocityGraph.vue